import { baseApi } from 'api/baseApiBuilder';
import { MethodENUM } from 'types/apiTypes/statisticApiTypes';
import type { UserActivityResponseType, GeneralAnalyticsResponseType, UserActivityPlotResponseType } from 'types/apiTypes/userAnalyticsApiTypes';
import type { UserActivityType, UserActivityPlotType } from 'pages/UserActivity/components/types';
import type { UserStatusDisplayType } from 'types/filtersType';
import type { CampaignRecipientType, CampaignType } from 'types/campaignTypes';
import type { GenerateLinkResponseType, IMagicLinkType, UserGeneralType, UserPartialType } from 'types/apiTypes/userApiTypes';
import type { CampaignPayloadType, NewCampaignPayloadType, CampaignResponseType } from 'types/apiTypes/campaignApiTypes';
import type { PromptType, UpdatePromptType } from 'types/apiTypes/promptTypes';
import type { FeedbacksResponseType, FeedbackType } from 'types/apiTypes/feedbackTypes';
import type { SettingItemType, UpdateSettingsPayloadType } from 'types/apiTypes/settingsApiTypes';

export interface IUser {
  id: number;
  created_at: string;
  email: string;
  is_active: boolean;
  is_staff: boolean;
  custom_data: {
    triggers?: string;
    quit_allies?: string;
    quiting_success_confidence?: number;
    continue_session_date?: null | string;
    name: string;
    reasons_to_quite?: string;
    reason_helps_higher_conf?: string;
    what_helped_quit_last_time?: string;
    HOW_ABLE_TO_QUITE_LAST_TIME?: string;
    SPECIAL_POPULATION_NON_ENGLISH?: boolean;
    [key: string]: unknown;
  };
}

export interface IMessage {
  id: number;
  message: string;
  sender: string;
  created_at: string;
  deleted_at: string;
}

enum DataApiPathENUM {
  USERS = '/api/users',
  MESSAGES = '/api/chats',
  SCHEDULER_CAMPAIGNS = '/api/scheduler/campaigns',
  CAMPAIGN_RECIPIENTS = '/api/campaign-recipients',
  PROMPTS = '/api/prompts',
  GENERAL = '/api/general',
  FEEDBACKS = '/api/feedbacks',
  SETTINGS = '/api/settings',
}

const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<UserGeneralType[], UserStatusDisplayType>({
      query: (userActivityStatus) => ({
        url: `${DataApiPathENUM.USERS}/`,
        method: MethodENUM.GET,
        params: {
          status: userActivityStatus,
        },
      }),
      providesTags: ['Users'],
    }),
    generateMagicLink: builder.mutation<GenerateLinkResponseType, { email: string }>({
      query: (data) => ({
        url: `${DataApiPathENUM.GENERAL}/get-link/`,
        method: MethodENUM.POST,
        data,
      }),
    }),
    getMagicLinks: builder.query<IMagicLinkType[], string>({
      query: (email) => ({
        url: `${DataApiPathENUM.GENERAL}/magic-links/`,
        method: MethodENUM.GET,
        params: { email },
      }),
    }),
    deleteMagicLink: builder.mutation<void, number>({
      query: (linkId) => ({
        url: `${DataApiPathENUM.GENERAL}/magic-links/${linkId}/`,
        method: MethodENUM.DELETE,
      }),
    }),
    getUserById: builder.query<UserPartialType, number>({
      query: (userId) => ({
        url: `${DataApiPathENUM.USERS}/${userId}/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['Users'],
    }),
    updateUserById: builder.mutation<IUser, { id: number; data: Partial<UserPartialType> }>({
      query: (params) => ({
        url: `${DataApiPathENUM.USERS}/${params.id}/`,
        method: MethodENUM.PUT,
        data: params.data,
      }),
      invalidatesTags: ['Users'],
    }),
  }),
  overrideExisting: false,
});

const conversationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMessages: builder.query<IMessage[], {
      userId: number | null;
      dateRange: {
        from: string | null;
        to: string | null;
      };
      search: string | null;
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.MESSAGES}/${params.userId}/`,
        method: MethodENUM.GET,
        params: {
          start_date: params.dateRange?.from?.toString(),
          end_date: params.dateRange?.to?.toString(),
          search: params.search,
        },
      }),
      providesTags: ['Messages'],
    }),
  }),
  overrideExisting: false,
});

const userAnalyticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGeneralAnalytics: builder.query({
      query: () => ({
        url: `${DataApiPathENUM.GENERAL}/analytics/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['GeneralAnalytics'],
      transformResponse(
        response: GeneralAnalyticsResponseType,
      ) {
        return {
          totalUsers: response.users_cnt,
          totalMessages: response.messages_cnt,
          mostLeavedStep: response.often_leave_at,
        };
      },
    }),
    getUserSummary: builder.query<UserActivityType, number>({
      query: (userId) => ({
        url: `${DataApiPathENUM.USERS}/${userId}/activity/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['UserSummary'],
      transformResponse(
        response: UserActivityResponseType,
      ) {
        const data = {
          status: response.status,
          userId: response.id,
          email: response.email,
          createdAt: response.created_at,
          daySinceFirstMessage: response.day_since_first_message,
          daySinceLastMessage: response.day_since_last_message,
          userMessagesCnt: response.user_messages_cnt,
          botMessagesCnt: response.bot_messages_cnt,
          quitPlanFinished: response.quit_plan_finished,
        };

        return {
          ...data,
        };
      },
    }),
    getUserActivityPlot: builder.query<UserActivityPlotType, {
      userId: number | null;
      dateRange: {
        from: string | null;
        to: string | null;
      };
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.USERS}/${params.userId}/plot/`,
        method: MethodENUM.GET,
        params: {
          start_date: params.dateRange?.from?.toString(),
          end_date: params.dateRange?.to?.toString(),
        },
      }),
      providesTags: ['UserActivityPlot'],
      transformResponse(
        response: UserActivityPlotResponseType,
      ) {
        const data = {
          dailyStats: response.daily_stats,
        };

        return {
          ...data,
        };
      },
    }),
    exportUsersDataToCSV: builder.mutation<Blob, void>({
      query: () => ({
        url: `${DataApiPathENUM.GENERAL}/user-csv/`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (response: Blob) => response,
    }),
  }),
});

const promptsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPromptsList: builder.query<PromptType[], void>({
      query: () => ({
        url: `${DataApiPathENUM.PROMPTS}/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['Prompts'],
    }),
    updatePrompt: builder.mutation<UpdatePromptType, {
      promptId: number;
      prompt: UpdatePromptType;
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.PROMPTS}/${params.promptId}/`,
        method: MethodENUM.PATCH,
        data: params.prompt,
      }),
      invalidatesTags: ['Prompts'],
    }),
  }),
});

const feedbacksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFeedbacks: builder.query<FeedbacksResponseType, {
      page: number | null;
      page_size: number | null;
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.FEEDBACKS}/`,
        method: MethodENUM.GET,
        params,
      }),
      providesTags: ['Feedbacks'],
    }),
  }),
});

const settingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettings: builder.query<SettingItemType[], void>({
      query: () => ({
        url: `${DataApiPathENUM.SETTINGS}/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['Settings'],
    }),
    updateSettings: builder.mutation<SettingItemType[], UpdateSettingsPayloadType>({
      query: (settings) => ({
        url: `${DataApiPathENUM.SETTINGS}/`,
        method: MethodENUM.PATCH,
        data: settings,
      }),
      invalidatesTags: ['Settings'],
    }),
  }),
});

const campaignApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCampaigns: builder.query<CampaignType[], void>({
      query: () => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['Campaigns'],
      transformResponse(
        response: CampaignResponseType[],
      ) {
        const newData: CampaignType[] = response.map((item) => {
          return {
            id: item.id,
            name: item.name,
            channel: item.channel,
            subject: item.subject,
            message: item.message,
            status: item.status,
            scheduledAt: item.scheduledAt,
            recipients: item.recipients.map((recipient) => {
              return {
                id: recipient.id,
                category: 'user' as CampaignRecipientType['category'],
                group: null,
                name: recipient?.custom_data?.name || recipient?.email || '',
                email: recipient?.email || null,
                phone: recipient?.phone,
                count: 1,
              };
            }),
          };
        });

        return newData;
      },
    }),
    addCampaign: builder.mutation<CampaignType, {
      userId: number;
      campaign: NewCampaignPayloadType;
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/`,
        method: MethodENUM.POST,
        data: params.campaign,
      }),
      invalidatesTags: ['Campaigns', 'UpcomingCampaigns'],
    }),
    updateCampaign: builder.mutation<CampaignType, {
      userId: number;
      campaign: CampaignPayloadType;
    }>({
      query: (params) => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/${params.campaign.id}/`,
        method: MethodENUM.PATCH,
        data: params.campaign,
      }),
      invalidatesTags: ['Campaigns', 'UpcomingCampaigns'],
    }),
    deleteCampaign: builder.mutation<void, {
      userId: number;
      campaignId: number;
    }>({
      query: (params) => ({
        // url: `${DataApiPathENUM.USERS}/${params.userId}/campaigns/${params.campaignId}/`,
        url: `${DataApiPathENUM.USERS}/{userId}/campaigns/{campaignId}/`,
        method: MethodENUM.DELETE,
      }),
      invalidatesTags: ['Campaigns', 'UpcomingCampaigns'],
    }),
    getUpcomingCampaigns: builder.query<CampaignType[], number>({
      query: (userId) => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/upcoming/`,
        method: MethodENUM.GET,
      }),
      providesTags: ['UpcomingCampaigns'],
      transformResponse(
        response: CampaignResponseType[],
      ) {
        const newData: CampaignType[] = response.map((item) => {
          return {
            id: item.id,
            name: item.name,
            channel: item.channel,
            subject: item.subject,
            message: item.message,
            status: item.status,
            scheduledAt: item.scheduledAt,
            recipients: item.recipients.map((recipient) => {
              return {
                id: recipient.id,
                category: 'user' as CampaignRecipientType['category'],
                group: null,
                name: recipient?.custom_data?.name || recipient?.email || '',
                email: recipient?.email || null,
                count: 1,
              };
            }),
          };
        });

        return newData;
      },
    }),
    updateCampaignStatus: builder.mutation<
      CampaignType,
      { userId: number; id: number; status: string }
    >({
      query: (params) => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/${params.id}/`,
        method: MethodENUM.PATCH,
        data: { status: params.status },
      }),
      invalidatesTags: ['Campaigns', 'UpcomingCampaigns'],
    }),
    generateMessage: builder.mutation<
      { message: string },
      { subject: string }
    >({

      query: (params) => ({
        url: `${DataApiPathENUM.SCHEDULER_CAMPAIGNS}/reminder-prompt/`,
        method: MethodENUM.POST,
        data: params,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGenerateMagicLinkMutation,
  useDeleteMagicLinkMutation,
  useGetMagicLinksQuery,
  useLazyGetUserByIdQuery,
  useUpdateUserByIdMutation,
} = userApi;

export const {
  useLazyGetMessagesQuery,
} = conversationApi;

export const {
  useGetPromptsListQuery,
  useUpdatePromptMutation,
} = promptsApi;

export const {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} = settingsApi;

export const {
  useGetFeedbacksQuery,
} = feedbacksApi;

export const {
  useGetGeneralAnalyticsQuery,
  useGetUserSummaryQuery,
  useLazyGetUserSummaryQuery,
  useGetUserActivityPlotQuery,
  useLazyGetUserActivityPlotQuery,
  useExportUsersDataToCSVMutation,
} = userAnalyticsApi;

export const {
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery,
  useAddCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useGetUpcomingCampaignsQuery,
  useLazyGetUpcomingCampaignsQuery,
  useUpdateCampaignStatusMutation,
  useGenerateMessageMutation,
} = campaignApi;
